import { loopObject } from '@/util/helpers/object-helpers';
import {
  LOADING,
  VIEW_SAVED,
  VIEW_DELETED,
  TABLE_ROWS_LOADED,
  VIEW_LOADED,
  VIEW_STATUS,
  VIEW_RETRYING,
  VIEWS_DATA_LOADED,
  MODAL_INTERACTION,
  CHANGE_TAB,
  STAR_CLICKED,
  LAYER_USER_INTERACTION,
  START_NEW_QUERY,
  VIEWS_TAGS_LOADED,
  CATEGORY_DELETED,
  REFRESH_RESULTS_CLICKED,
  CHANGE_DATASOURCE,
  RESET_DATASOURCE,
  AGGREGATE_CONFIGURATION_CHANGED,
  INCREMENT_SAVED_QUERIES_COUNT,
} from '../actions/constants';
import ViewRef from '../util/ViewRef';
import { VIEWS_TAB } from '../util/enums/EnumModals';
import { VIEWS } from '../util/enums/views.constants';
import CategoryRef from '../util/CategoryRef';
import { ALL } from '../views/SavedQueries/consts';

const INITIAL_STATE = {
  showShareBar: false, // true if user is looking at an unmodified view
  statusLoaded: false, // true when status has been loaded
  errorMsg: null, // status error message
  lastLoadedView: null, // ViewRef of the view that was loaded (might not belong to current user)
  cachedTime: null, // Timestamp string (see formatTimestamp(...)). null unless there is a cached version of this view available for instant load
  retryUsed: false,

  // views tab
  allViews: null, // list of [ViewRef, email] array objs
  /**
   * A temporary solution to make ENG-497 work as expected,
   * since we set allViews => null when we navigate e.g. to Query Builder.
   * It's done to free browser memory, according to the comment related to
   * CHANGE_TAB action handling.
   */
  savedQueriesCount: 0,
  favourites: [], // list of 'series' ids
  // filters
  search: '', // string representing what user has searched for
  tags: [], // List of available tags that are related to views
  filter: ALL, // string representing filter user has applied
  categoryFilter: ALL, // string representing category (tags) filter user has applied
  lockLastLoadedView: false, // a workaround to not reset current view to `null` on `Refresh results` click
};

function interaction(state, payload) {
  switch (payload.section) {
    case 'search': {
      return { ...state, search: payload.value };
    }
    case 'filter': {
      return {
        ...state,
        filter: payload.value ? payload.value : ALL,
        categoryFilter: payload.categoryFilter,
      };
    }
  }
  return state;
}

const sortViews = (views) => {
  return views.sort((a, b) => b[0].saved().localeCompare(a[0].saved()));
};

function updateViews(state, payload) {
  if (!state.allViews) {
    // nothing to update
    return state;
  }
  // my views have been updated, so replace them
  const myViews = payload.json.map((v) => ViewRef.build(v));
  const viewsWithoutMine = state.allViews.filter(
    (entry) => !myViews.some((v) => v.series() === entry[0].series()),
  ); // remove existing
  const allViews = [
    ...viewsWithoutMine,
    ...myViews.map((v) => [v, payload.userEmail]),
  ];
  return {
    ...state,
    allViews: sortViews(allViews),
    savedQueriesCount: allViews.length,
  };
}

export function deserialiseViews(viewsFromServer) {
  const allViews = [];
  loopObject(viewsFromServer, (key, value) => {
    value.forEach((v) => allViews.push([ViewRef.build(v), key]));
  });
  return sortViews(allViews);
}

const Views = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case INCREMENT_SAVED_QUERIES_COUNT: {
      return {
        ...state,
        savedQueriesCount: state.savedQueriesCount + 1,
      };
    }
    case START_NEW_QUERY: {
      // reset all
      return {
        ...INITIAL_STATE,
        savedQueriesCount: state.savedQueriesCount,
        allViews: state.allViews, // keep saved queries
      };
    }
    case RESET_DATASOURCE:
    case CHANGE_DATASOURCE: {
      return { ...INITIAL_STATE };
    }
    case MODAL_INTERACTION: {
      if (action.payload.modalType !== VIEWS_TAB) {
        return state;
      }
      return interaction(state, action.payload.props);
    }
    case VIEWS_DATA_LOADED: {
      const allViews = deserialiseViews(action.payload.json._views);
      return {
        ...state,
        allViews,
        savedQueriesCount: allViews.length,
        favourites: action.payload.json._favourites.map((series) => series),
      };
    }
    case VIEWS_TAGS_LOADED: {
      return {
        ...state,
        tags: action.payload.json.map((e) => CategoryRef.build(e)),
      };
    }
    case CHANGE_TAB: {
      if (action.payload.tab !== VIEWS) {
        return {
          ...state, // search: '', //reset search
          allViews: null, // reset (free up browser memory)
        };
      }
      return state;
    }
    case LAYER_USER_INTERACTION:
    case AGGREGATE_CONFIGURATION_CHANGED: {
      if (
        action.type === LAYER_USER_INTERACTION &&
        !action.payload.chartingOptions
      ) {
        return state;
      }
      // reset to INITIAL_STATE so that "discard changes" appears when charting options updated
      return {
        ...INITIAL_STATE,
        savedQueriesCount: state.savedQueriesCount,
        allViews: state.allViews, // keep saved queries
        filter: state.filter, // keep filter
        categoryFilter: state.categoryFilter, // keep category filter
        search: state.search, // keep search
      };
    }
    case REFRESH_RESULTS_CLICKED:
      return { ...state, lockLastLoadedView: true };
    case LOADING:
      if (action.awaiting === TABLE_ROWS_LOADED) {
        if (!action.meta.loadingView) {
          return {
            ...INITIAL_STATE,
            savedQueriesCount: state.savedQueriesCount,
            allViews: state.allViews, // keep saved queries
            lastLoadedView:
              state.lastLoadedView && state.lockLastLoadedView
                ? state.lastLoadedView
                : null,
            filter: state.filter, // keep filter
            categoryFilter: state.categoryFilter, // keep category filter
            search: state.search, // keep search
          }; // reset
        }
        return {
          ...state,
          retryUsed: false, // reset
        };
      }
      if (action.awaiting === VIEWS_DATA_LOADED) {
        return {
          ...state,
          allViews: null, // reset
        };
      }
      if (action.awaiting === STAR_CLICKED) {
        const seriesId = action.meta.view.seriesId ?? action.meta.view._series;
        if (action.meta.on) {
          return {
            ...state,
            favourites: [...state.favourites, seriesId],
          };
        }
        return {
          ...state,
          favourites: state.favourites.filter((series) => series !== seriesId),
        };
      }
      if (action.awaiting === VIEW_STATUS) {
        return {
          ...state,
          cachedTime: null, // reset
          statusLoaded: false,
        };
      }
      return state;
    case VIEW_DELETED: {
      const allViews = state.allViews.filter(
        (entry) => entry[0].series() !== action.payload.deleted.series(),
      );
      return {
        ...state,
        savedQueriesCount: allViews.length,
        allViews,
      };
    }
    case VIEW_SAVED:
      if (action.payload.renaming) {
        let newState = state;
        if (
          state.lastLoadedView &&
          state.lastLoadedView.series() === action.payload.savedView.series()
        ) {
          // the loaded view is the one that was renamed, so make sure we update it
          newState = { ...newState, lastLoadedView: action.payload.savedView };
        }
        // view is renamed, so rename globally in reducer
        return updateViews(newState, action.payload);
      }
      return {
        ...state,
        lastLoadedView: action.payload.savedView,
        showShareBar: true,
        statusLoaded: false,
      };
    case VIEW_RETRYING:
      return {
        ...state,
        errorMsg: null, // reset
        statusLoaded: false,
        retryUsed: true,
      };
    case VIEW_LOADED:
      return {
        ...state,
        lastLoadedView: action.payload.view,
        cachedTime: null, // reset
        showShareBar: true,
        statusLoaded: false,
        favourites: action.payload.json._isFavourite
          ? [action.payload.view.series()]
          : [],
      };
    case VIEW_STATUS:
      return {
        ...state,
        statusLoaded: action.payload.json._status_loaded,
        errorMsg: action.payload.json._error_msg,
        cachedTime: action.payload.json._cached_time,
      };
    case CATEGORY_DELETED:
      return {
        ...state,
        filter: ALL, // reset filters
        categoryFilter: ALL, // reset filters
      };
    default:
      return state;
  }
};

export default Views;
